import { useCallback, type FocusEvent } from "react";
import type { HoverEvent } from "react-aria-components";
import { Link as RouterLink } from "react-router";
import { Link, type LinkProps } from "@/components/ui/link.tsx";
import { useLinkIntent } from "./useLinkIntent.ts";

const DEFAULT_LINK_HOVER = "hover:text-foreground hover:underline underline-offset-4";

export interface RoutedLinkProps extends LinkProps {
    prefetch?: () => void;
}

export function RoutedLink({ className, prefetch, onHoverStart, onHoverEnd, onFocus, ...props }: RoutedLinkProps) {
    const composedClassName = "href" in props && typeof className !== "function" ? `${DEFAULT_LINK_HOVER} ${className ?? ""}`.trim() : className;
    const { href } = props;

    const linkIntent = useLinkIntent(href, { prefetch });

    const handleHoverStart = useCallback(
        (event: HoverEvent) => {
            onHoverStart?.(event);
            linkIntent.start();
        },
        [onHoverStart, linkIntent]
    );

    const handleHoverEnd = useCallback(
        (event: HoverEvent) => {
            onHoverEnd?.(event);
            linkIntent.end();
        },
        [onHoverEnd, linkIntent]
    );

    const handleFocus = useCallback(
        (event: FocusEvent) => {
            onFocus?.(event);
            linkIntent.focus();
        },
        [onFocus, linkIntent]
    );

    return (
        // oxlint-disable-next-line jsx-a11y/anchor-has-content -- a wrapper: the content arrives through the spread props
        <Link
            {...props}
            className={composedClassName}
            onHoverStart={handleHoverStart}
            onHoverEnd={handleHoverEnd}
            onFocus={handleFocus}
            render={domProps => {
                if ("href" in domProps) {
                    const { href: domHref, ...rest } = domProps;

                    return <RouterLink to={domHref} {...rest} />;
                }

                return <span {...domProps} />;
            }}
        />
    );
}
